import {IFiltersConfigPayload} from "@/app/models/auctor";
import {notFound} from "next/navigation";

export const BASE_API: string = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api`
export const BASE_NEXT_API: string = `${process.env.NEXT_PUBLIC_RM_NEXT_URL}/api`
export const BASE_AUCTOR_API: string = `${process.env.NEXT_PUBLIC_RM_BASE_AUCTOR_API}/api/auctor/front`

export const AUCTOR_CACHE_TOOL = "AUCTOR_CACHE_TOOL"
// Miroir de NextCacheRevalidator::COMPARATOR_TAG (reassurezmoi) : le backend revalide ce tag après chaque synchro Google Sheet
export const AUCTOR_CACHE_COMPARATOR = "AUCTOR_CACHE_COMPARATOR"

export const ENDPOINTS = {
    GET_AUCTOR_SLUGS: `${BASE_AUCTOR_API}/page/slugs`,
    GET_PAGE_CONFIG: `${BASE_AUCTOR_API}/page/config/by-slug?slug=%SLUG%`,
    GET_PAGE_CONTENT: `${BASE_AUCTOR_API}/page/content/by-slug?slug=%SLUG%`,
    GET_PAGE_COMMENTS: `${BASE_AUCTOR_API}/page/comments/by-slug?slug=%SLUG%`,
    GET_COMPARATOR_FILTERS: `${BASE_AUCTOR_API}/comparator/filters-config/%COMPARATOR_ID%`,
    GET_TOOL: `${BASE_AUCTOR_API}/tool/%TOOL_ID%`,
    GET_COMPARATOR_TEMPLATE: `${BASE_AUCTOR_API}/comparator/template-config/%COMPARATOR_ID%`,
    GET_COMPARATOR_DATA: `${BASE_AUCTOR_API}/comparator/offers-data/%COMPARATOR_ID%`,
    GET_COMPARATOR_INITIAL_DATA_BY_ID: `${BASE_NEXT_API}/auctor/initial-offers?comparatorId=%COMPARATOR_ID%`,
    GET_COMPARATOR_INITIAL_DATA_BY_SLUG: `${BASE_NEXT_API}/auctor/initial-offers?slug=%SLUG%`,
    GET_RM_NOTATION: `${BASE_API}/reviews/get-notation`,
}

export const AUCTOR_CACHE_TAGS_TO_DESCRIPTION = [
    {
        tag: AUCTOR_CACHE_TOOL,
        description: "Auctor — outils (tool)",
        urls: [ENDPOINTS.GET_TOOL, ENDPOINTS.GET_COMPARATOR_TEMPLATE]
    },
    {
        tag: AUCTOR_CACHE_COMPARATOR,
        description: "Auctor — comparateurs (offres et filtres)",
        urls: [
            ENDPOINTS.GET_COMPARATOR_DATA,
            ENDPOINTS.GET_COMPARATOR_FILTERS,
            ENDPOINTS.GET_COMPARATOR_INITIAL_DATA_BY_ID,
            ENDPOINTS.GET_COMPARATOR_INITIAL_DATA_BY_SLUG,
        ]
    }
]

export async function getAuctorPageConfig(slug: string, cache: boolean = true) {
    const cachePolicy = cache ? 'force-cache' : 'no-store'
    const response = await fetch(ENDPOINTS.GET_PAGE_CONFIG.replace('%SLUG%', slug), {cache: cachePolicy});

    if (!response.ok && process.env.APP_ENV === 'prod') {
        return notFound()
    }

    return response.json();
}

export async function getComparatorTemplateConfig(comparatorId: number, cache: boolean = true) {
    try {
        const cachePolicy = cache ? 'force-cache' : 'no-store'
        const response = await fetch(ENDPOINTS.GET_COMPARATOR_TEMPLATE.replace('%COMPARATOR_ID%', comparatorId?.toString()), {
            cache: cachePolicy,
            next: {tags: [AUCTOR_CACHE_TOOL]},
        })
        console.log(ENDPOINTS.GET_COMPARATOR_TEMPLATE.replace('%COMPARATOR_ID%', comparatorId?.toString()))
        if (!response.ok && process.env.APP_ENV === 'prod') {
            console.error(`Error fetching comparator template config: ${response.status} ${response.statusText}`);
            return notFound()
        }

        return response.json();
    } catch (error) {
        console.error('Error in getComparatorTemplateConfig:', error);
        if (process.env.APP_ENV === 'prod') {
            return notFound();
        }
        throw error;
    }
}

export async function getComparatorFiltersConfig(comparatorId: number, cache: boolean = true): Promise<IFiltersConfigPayload> {
    try {
        const cachePolicy = cache ? 'force-cache' : 'no-store'
        const response = await fetch(ENDPOINTS.GET_COMPARATOR_FILTERS.replace('%COMPARATOR_ID%', comparatorId?.toString()), {
            cache: cachePolicy,
            next: {tags: [AUCTOR_CACHE_COMPARATOR]},
        });
        console.log(ENDPOINTS.GET_COMPARATOR_FILTERS.replace('%COMPARATOR_ID%', comparatorId?.toString()))
        if (!response.ok && process.env.APP_ENV === 'prod') {
            console.error(`Error fetching comparator filters config: ${response.status} ${response.statusText}`);
            return notFound()
        }

        return response.json();
    } catch (error) {
        console.error('Error in getComparatorFiltersConfig:', error);
        if (process.env.APP_ENV === 'prod') {
            return notFound();
        }
        throw error;
    }
}

// Offer sheet URLs point to the prod guide: rebase /guide/partenaire/* links on the env WP host (no-op in prod)
const PROD_GUIDE_HOST = "https://guide.reassurez-moi.fr"
const PROD_GUIDE_PARTNER_PREFIX = `${PROD_GUIDE_HOST}/guide/partenaire/`

function rebasePartnerLinksOnEnvGuideHost(rows: any): any {
    const wordpressHost = process.env.NEXT_PUBLIC_RM_WORDPRESS_API
    if (!wordpressHost || wordpressHost === PROD_GUIDE_HOST || !Array.isArray(rows)) {
        return rows
    }

    return rows.map((row) => {
        if (!row || typeof row !== "object") {
            return row
        }
        const rebased: Record<string, unknown> = {...row}
        for (const [column, value] of Object.entries(rebased)) {
            if (typeof value === "string" && value.startsWith(PROD_GUIDE_PARTNER_PREFIX)) {
                rebased[column] = wordpressHost + value.slice(PROD_GUIDE_HOST.length)
            }
        }
        return rebased
    })
}

export async function getComparatorResultsData(comparatorId: number, cache: boolean = true) {
    try {
        const response = await fetch(ENDPOINTS.GET_COMPARATOR_DATA.replace('%COMPARATOR_ID%', comparatorId?.toString()), {
            cache: 'force-cache',
            next: {tags: [AUCTOR_CACHE_COMPARATOR]},
        });

        if (!response.ok) {
            console.error(`Error fetching comparator results data: ${response.status} ${response.statusText}`);
            if (process.env.APP_ENV === 'prod') {
                return notFound()
            }
            return [];
        }

        const data = await response.json();
        if (!data) {
            console.error('Empty response from comparator results data');
            return [];
        }

        return rebasePartnerLinksOnEnvGuideHost(data);
    } catch (error) {
        console.error('Error in getComparatorResultsData:', error);
        if (process.env.APP_ENV === 'prod') {
            return notFound();
        }
        return [];
    }
}

export async function getComparatorInitialData({
    comparatorId,
    slug,
}: {
    comparatorId?: number;
    slug?: string;
}) {
    try {
        if (!comparatorId && !slug) {
            throw new Error('Either comparatorId or slug must be provided');
        }

        let endpoint = '';
        if (slug) {
            endpoint = ENDPOINTS.GET_COMPARATOR_INITIAL_DATA_BY_SLUG.replace('%SLUG%', slug);
        } else if (comparatorId) {
            endpoint = ENDPOINTS.GET_COMPARATOR_INITIAL_DATA_BY_ID.replace(
                '%COMPARATOR_ID%',
                comparatorId.toString()
            );
        }

        const response = await fetch(endpoint, {
            cache: 'force-cache',
            next: {tags: [AUCTOR_CACHE_COMPARATOR]},
        });

        if (!response.ok) {
            console.error(`Error fetching comparator initial data: ${response.status} ${response.statusText}`);
            if (process.env.APP_ENV === 'prod') {
                return notFound();
            }
            return [];
        }

        return response.json();
    } catch (error) {
        console.error('Error in getComparatorInitialData:', error);
        if (process.env.APP_ENV === 'prod') {
            return notFound();
        }
        return [];
    }
}

export async function getTool(toolId: number, cache: boolean = true) {
    const cachePolicy = cache ? 'force-cache' : 'no-store'
    const url = ENDPOINTS.GET_TOOL.replace('%TOOL_ID%', toolId?.toString())
    const response = await fetch(url, {
        cache: cachePolicy,
        next: {tags: [AUCTOR_CACHE_TOOL]},
    });

    if (!response.ok && process.env.APP_ENV === 'prod') {
        return notFound()
    }

    return response.json();
}

export async function getNotation() {
    const response = await fetch(ENDPOINTS.GET_RM_NOTATION, {cache: 'force-cache'});

    return response.json();
}

export async function fetchReviewData(): Promise<{ notation: number, reviewNumber: number }> {
    const reviewData = await getNotation();
    if (reviewData.notation > 5) {
        reviewData.notation /= 2;
    }

    return reviewData;
}

export async function getExistingAuctorSlugs(cache: boolean = true) {
    const cachePolicy = cache ? 'force-cache' : 'no-store'
    const response = await fetch(ENDPOINTS.GET_AUCTOR_SLUGS, {cache: cachePolicy})

    return response.json()
}
